import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, Modal, TextInput, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useLocalSearchParams } from 'expo-router';
import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import InlineConfirm from '../../src/components/InlineConfirm';
import InlineMessage from '../../src/components/InlineMessage';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive, rs } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';

interface PlanItem {
  id: string; name: string; tierName: string | null; priceMonthly: number; isActive: boolean;
  businessName: string; subscriberCount: number; createdAt: string;
  description?: string;
  planType?: 'automatic' | 'self_serve' | 'mixed';
  maxWashesPerDay?: number | null; maxWashesPerMonth?: number | null;
  maxMinutesPerDay?: number | null; maxMinutesPerMonth?: number | null;
}

interface Subscriber {
  id: string; userId: string; name: string; email: string; accountNumber: string;
  status: string; currentPeriodEnd: string | null;
  washesUsedToday: number; washesUsedThisMonth: number;
  minutesUsedToday?: number; minutesUsedThisMonth?: number;
  cancelAtPeriodEnd: boolean;
}

interface UserResult {
  id: string; name: string; email: string; accountNumber: string;
}

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const subStatusColor = (s: string) => {
  if (s === 'active') return '#059669';
  if (s === 'past_due') return '#d97706';
  return '#dc2626';
};

/** AdminSubscriptionPlans - list, search, and manage subscription membership plans. */
export default function AdminSubscriptionPlans() {
  const { ownerId: paramOwnerId } = useLocalSearchParams<{ ownerId?: string }>();
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < 768;
  const styles = useStyles(colors, isMobile);
  const modalBottomPadding = useModalBottomPadding();
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editItem, setEditItem] = useState<PlanItem | null>(null);
  const [editData, setEditData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [subscribersPlan, setSubscribersPlan] = useState<PlanItem | null>(null);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  // Subscriber modal management state
  const [subSearch, setSubSearch] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<UserResult[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [removingSubId, setRemovingSubId] = useState<string | null>(null);
  const [confirmRemoveSub, setConfirmRemoveSub] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchPlans = useCallback(async (p = 1, s = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      if (paramOwnerId) params.set('ownerId', paramOwnerId);
      const data = await api<{ plans: PlanItem[]; total: number }>(`/api/admin/subscription-plans?${params}`);
      if (signal?.aborted) return;
      setPlans(data.plans);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, [paramOwnerId]);

  useEffect(() => { const ac = new AbortController(); fetchPlans(1, '', ac.signal); return () => ac.abort(); }, [paramOwnerId]);

  const openEdit = (item: PlanItem) => {
    setEditItem(item);
    setEditData({
      name: item.name,
      planType: item.planType ?? 'automatic',
      tierName: item.tierName ?? '',
      priceMonthly: String(item.priceMonthly),
      maxWashesPerDay: item.maxWashesPerDay != null ? String(item.maxWashesPerDay) : '',
      maxWashesPerMonth: item.maxWashesPerMonth != null ? String(item.maxWashesPerMonth) : '',
      maxMinutesPerDay: item.maxMinutesPerDay != null ? String(item.maxMinutesPerDay) : '',
      maxMinutesPerMonth: item.maxMinutesPerMonth != null ? String(item.maxMinutesPerMonth) : '',
      isActive: item.isActive,
    });
  };

  const handleSave = async () => {
    if (!editItem || !editData) return;
    setSaving(true);
    try {
      const includesAuto = editData.planType === 'automatic' || editData.planType === 'mixed';
      const includesSelf = editData.planType === 'self_serve' || editData.planType === 'mixed';
      await api(`/api/admin/subscription-plans/${editItem.id}`, {
        method: 'PUT',
        body: {
          name: editData.name,
          planType: editData.planType,
          // Tier is set from the wash-tier catalog at creation and is immutable here
          // (omitting tierName leaves it untouched; avoids desyncing tierId/tierName).
          priceMonthly: parseInt(editData.priceMonthly) || 0,
          maxWashesPerDay: includesAuto ? (parseInt(editData.maxWashesPerDay) || null) : null,
          maxWashesPerMonth: includesAuto ? (parseInt(editData.maxWashesPerMonth) || null) : null,
          maxMinutesPerDay: includesSelf ? (parseInt(editData.maxMinutesPerDay) || null) : null,
          maxMinutesPerMonth: includesSelf ? (parseInt(editData.maxMinutesPerMonth) || null) : null,
          isActive: editData.isActive,
        },
      });
      setEditItem(null);
      fetchPlans(page, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setSaving(false); }
  };

  const openSubscribers = async (plan: PlanItem) => {
    setSubscribersPlan(plan);
    setSubSearch('');
    setUserSearchQuery('');
    setUserSearchResults([]);
    setLoadingSubs(true);
    try {
      const data = await api<{ subscribers: Subscriber[] }>(`/api/admin/subscription-plans/${plan.id}/subscribers`);
      setSubscribers(data.subscribers);
    } catch (err) { console.error(err); }
    finally { setLoadingSubs(false); }
  };

  const refreshSubscribers = async () => {
    if (!subscribersPlan) return;
    try {
      const data = await api<{ subscribers: Subscriber[] }>(`/api/admin/subscription-plans/${subscribersPlan.id}/subscribers`);
      setSubscribers(data.subscribers);
    } catch (err) { console.error(err); }
  };

  const handleRemoveSubscriber = (sub: Subscriber) => {
    setConfirmRemoveSub(sub.id);
  };

  const confirmRemove = async () => {
    const sub = subscribers.find(s => s.id === confirmRemoveSub);
    if (!sub) return;
    setRemovingSubId(sub.id);
    setConfirmRemoveSub(null);
    try {
      await api(`/api/admin/subscriptions/${sub.id}`, { method: 'DELETE' });
      await refreshSubscribers();
      fetchPlans(page, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setRemovingSubId(null); }
  };

  const handleSearchUsers = async () => {
    if (!userSearchQuery.trim()) return;
    setSearchingUsers(true);
    try {
      const data = await api<{ users: UserResult[] }>(`/api/admin/users?search=${encodeURIComponent(userSearchQuery)}&limit=10`);
      // Filter out users who are already subscribers
      const existingUserIds = new Set(subscribers.map(s => s.userId));
      setUserSearchResults(data.users.filter(u => !existingUserIds.has(u.id)));
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setSearchingUsers(false); }
  };

  const handleAddSubscriber = async (userId: string) => {
    if (!subscribersPlan) return;
    setAddingUser(true);
    try {
      await api('/api/admin/subscriptions', { method: 'POST', body: { userId, planId: subscribersPlan.id } });
      setUserSearchQuery('');
      setUserSearchResults([]);
      await refreshSubscribers();
      fetchPlans(page, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setAddingUser(false); }
  };

  const filteredSubscribers = subSearch
    ? subscribers.filter(s => s.name.toLowerCase().includes(subSearch.toLowerCase()) || s.email.toLowerCase().includes(subSearch.toLowerCase()))
    : subscribers;

  const handleDeletePlan = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api(`/api/admin/subscription-plans/${confirmDelete}`, { method: 'DELETE' });
      setConfirmDelete(null);
      setInlineMsg({ type: 'success', message: 'Subscription plan deleted' });
      fetchPlans(page, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setDeleting(false); }
  };

  const csvEndpoint = `/api/admin/subscription-plans?${new URLSearchParams({ ...(search && { search }) })}`;

  const renderItem = ({ item }: { item: PlanItem }) => (
    <View style={styles.row}>
      <View style={styles.rowBody}>
        <View style={styles.rowHeader}>
          <Text style={styles.name}>{item.name}</Text>
          <View style={[styles.badge, { backgroundColor: item.isActive ? '#05966915' : '#dc262615' }]}>
            <Text style={[styles.badgeText, { color: item.isActive ? '#059669' : '#dc2626' }]}>
              {item.isActive ? 'Active' : 'Inactive'}
            </Text>
          </View>
        </View>
        <Text style={styles.meta}>
          {item.planType === 'self_serve' ? 'Self-serve' : item.planType === 'mixed' ? `Mixed${item.tierName ? ` · ${item.tierName}` : ''}` : `Tier: ${item.tierName ?? '—'}`} · {fmt(item.priceMonthly)}/mo ·{' '}
          <Text style={{ color: colors.primary, textDecorationLine: 'underline' }} onPress={() => openSubscribers(item)}>
            {item.subscriberCount} subscribers
          </Text>
        </Text>
        <Text style={styles.subText}>Owner: {item.businessName}</Text>
      </View>
      <TouchableOpacity onPress={() => openEdit(item)} hitSlop={8}>
        <Ionicons name="create-outline" size={18} color={colors.primary} />
      </TouchableOpacity>
      <TouchableOpacity onPress={() => setConfirmDelete(item.id)} hitSlop={8}>
        <Ionicons name="trash-outline" size={18} color={colors.danger} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={() => fetchPlans(1, search)}
        total={total} page={page} limit={20} onPageChange={(p) => fetchPlans(p, search)}
        csvEndpoint={csvEndpoint} csvFilename="subscription-plans.csv" label="plan"
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />} data={plans} keyExtractor={item => item.id} renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No subscription plans found</Text>}
        />
      )}

      <InlineConfirm
        message={`Delete subscription plan "${plans.find(p => p.id === confirmDelete)?.name ?? ''}"?`}
        visible={!!confirmDelete}
        onConfirm={handleDeletePlan}
        onCancel={() => setConfirmDelete(null)}
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
      />
      <InlineMessage type={inlineMsg?.type || 'error'} message={inlineMsg?.message || ''} visible={!!inlineMsg && !editItem && !subscribersPlan} onDismiss={() => setInlineMsg(null)} />

      {/* Subscribers Modal */}
      <Modal visible={!!subscribersPlan} transparent animationType="slide" onRequestClose={() => setSubscribersPlan(null)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, !isMobile && { maxWidth: 500 }, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Subscribers — {subscribersPlan?.name}</Text>

            {/* Search existing subscribers */}
            <View style={styles.subSearchRow}>
              <Ionicons name="search" size={16} color={colors.textLight} />
              <TextInput
                style={styles.subSearchInput}
                placeholder="Filter subscribers..."
                placeholderTextColor={colors.textLight}
                value={subSearch}
                onChangeText={setSubSearch}
                autoCapitalize="none"
                maxLength={100}
              />
              {subSearch.length > 0 && (
                <TouchableOpacity onPress={() => setSubSearch('')}>
                  <Ionicons name="close-circle" size={16} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            {loadingSubs ? (
              <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.lg }} />
            ) : (
              <ScrollView style={{ maxHeight: 250 }} nestedScrollEnabled>
                {filteredSubscribers.map(sub => (
                  <View key={sub.id} style={styles.subRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.name}>{sub.name}</Text>
                      <Text style={styles.meta}>{sub.email} · #{sub.accountNumber}</Text>
                      <Text style={styles.subText}>
                        Today: {sub.washesUsedToday} · Month: {sub.washesUsedThisMonth}
                        {sub.currentPeriodEnd ? ` · Ends: ${new Date(sub.currentPeriodEnd).toLocaleDateString()}` : ''}
                      </Text>
                    </View>
                    <View style={[styles.badge, { backgroundColor: subStatusColor(sub.status) + '15' }]}>
                      <Text style={[styles.badgeText, { color: subStatusColor(sub.status) }]}>
                        {sub.cancelAtPeriodEnd ? 'Canceling' : sub.status}
                      </Text>
                    </View>
                    {sub.status !== 'canceled' && (
                      <TouchableOpacity onPress={() => handleRemoveSubscriber(sub)} disabled={removingSubId === sub.id} hitSlop={8}>
                        <Ionicons name="close-circle" size={22} color={removingSubId === sub.id ? colors.textLight : colors.danger} />
                      </TouchableOpacity>
                    )}
                  </View>
                ))}
                {filteredSubscribers.length === 0 && <Text style={styles.empty}>No subscribers</Text>}
              </ScrollView>
            )}

            <InlineConfirm
              message={`Cancel subscription for ${subscribers.find(s => s.id === confirmRemoveSub)?.name ?? ''}?`}
              visible={!!confirmRemoveSub}
              onConfirm={confirmRemove}
              onCancel={() => setConfirmRemoveSub(null)}
              confirmLabel="Remove"
              loading={removingSubId === confirmRemoveSub}
              variant="danger"
            />

            {/* Add Subscriber Section */}
            <View style={styles.addSection}>
              <Text style={styles.addSectionTitle}>Add Subscriber</Text>
              <View style={styles.userSearchRow}>
                <TextInput
                  style={styles.userSearchInput}
                  placeholder="Search users by name or email..."
                  placeholderTextColor={colors.textLight}
                  value={userSearchQuery}
                  onChangeText={setUserSearchQuery}
                  onSubmitEditing={handleSearchUsers}
                  autoCapitalize="none"
                  maxLength={100}
                />
                <TouchableOpacity style={styles.searchBtn} onPress={handleSearchUsers} disabled={searchingUsers}>
                  {searchingUsers ? (
                    <ActivityIndicator size="small" color={colors.white} />
                  ) : (
                    <Ionicons name="search" size={16} color={colors.white} />
                  )}
                </TouchableOpacity>
              </View>
              {userSearchResults.length > 0 && (
                <ScrollView style={{ maxHeight: 120 }} nestedScrollEnabled>
                  {userSearchResults.map(user => (
                    <View key={user.id} style={styles.userResultRow}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.name}>{user.name}</Text>
                        <Text style={styles.meta}>{user.email} · #{user.accountNumber}</Text>
                      </View>
                      <TouchableOpacity
                        style={[styles.addBtn, addingUser && { opacity: 0.5 }]}
                        onPress={() => handleAddSubscriber(user.id)}
                        disabled={addingUser}
                      >
                        <Ionicons name="add" size={16} color={colors.white} />
                        <Text style={styles.addBtnText}>Add</Text>
                      </TouchableOpacity>
                    </View>
                  ))}
                </ScrollView>
              )}
            </View>

            <InlineMessage
              type={inlineMsg?.type ?? 'error'}
              message={inlineMsg?.message ?? ''}
              visible={!!inlineMsg && !!subscribersPlan}
              onDismiss={() => setInlineMsg(null)}
            />

            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setSubscribersPlan(null)}>
                <Text style={styles.cancelText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={!!editItem} transparent animationType="slide" onRequestClose={() => setEditItem(null)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Edit Subscription Plan</Text>
            {editData && (
              <ScrollView nestedScrollEnabled>
                <Text style={styles.fieldLabel}>Plan Name</Text>
                <TextInput style={styles.fieldInput} value={editData.name} onChangeText={(v) => setEditData({ ...editData, name: v })} maxLength={32} />
                <Text style={styles.fieldLabel}>Plan Type</Text>
                <View style={{ flexDirection: 'row', gap: 8, marginBottom: 8 }}>
                  {(['automatic', 'self_serve', 'mixed'] as const).map(pt => (
                    <TouchableOpacity
                      key={pt}
                      onPress={() => setEditData({ ...editData, planType: pt })}
                      style={{
                        paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, borderWidth: 1,
                        borderColor: editData.planType === pt ? colors.primary : colors.borderLight,
                        backgroundColor: editData.planType === pt ? colors.primary : 'transparent',
                      }}
                    >
                      <Text style={{ color: editData.planType === pt ? '#fff' : colors.text, fontSize: 12 }}>
                        {pt === 'automatic' ? 'Auto' : pt === 'self_serve' ? 'Self-serve' : 'Mixed'}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {(editData.planType === 'automatic' || editData.planType === 'mixed') && (
                  <>
                    <Text style={styles.fieldLabel}>Wash Tier</Text>
                    <Text style={[styles.fieldInput, { color: colors.textSecondary }]}>{editData.tierName || '—'} (set at creation)</Text>
                  </>
                )}
                <Text style={styles.fieldLabel}>Price Monthly (cents)</Text>
                <TextInput style={styles.fieldInput} value={editData.priceMonthly} onChangeText={(v) => setEditData({ ...editData, priceMonthly: v })} keyboardType="number-pad" maxLength={10} />
                {(editData.planType === 'automatic' || editData.planType === 'mixed') && (
                  <>
                    <Text style={styles.fieldLabel}>Max Washes/Day</Text>
                    <TextInput style={styles.fieldInput} value={editData.maxWashesPerDay} onChangeText={(v) => setEditData({ ...editData, maxWashesPerDay: v })} keyboardType="number-pad" maxLength={6} />
                    <Text style={styles.fieldLabel}>Max Washes/Month</Text>
                    <TextInput style={styles.fieldInput} value={editData.maxWashesPerMonth} onChangeText={(v) => setEditData({ ...editData, maxWashesPerMonth: v })} keyboardType="number-pad" maxLength={6} />
                  </>
                )}
                {(editData.planType === 'self_serve' || editData.planType === 'mixed') && (
                  <>
                    <Text style={styles.fieldLabel}>Max Minutes/Day (self-serve)</Text>
                    <TextInput style={styles.fieldInput} value={editData.maxMinutesPerDay} onChangeText={(v) => setEditData({ ...editData, maxMinutesPerDay: v })} keyboardType="number-pad" maxLength={6} />
                    <Text style={styles.fieldLabel}>Max Minutes/Month (self-serve)</Text>
                    <TextInput style={styles.fieldInput} value={editData.maxMinutesPerMonth} onChangeText={(v) => setEditData({ ...editData, maxMinutesPerMonth: v })} keyboardType="number-pad" maxLength={6} />
                  </>
                )}
                <TouchableOpacity style={styles.toggleRow} onPress={() => setEditData({ ...editData, isActive: !editData.isActive })}>
                  <Ionicons name={editData.isActive ? 'checkmark-circle' : 'close-circle'} size={24} color={editData.isActive ? '#059669' : '#dc2626'} />
                  <Text style={styles.toggleText}>{editData.isActive ? 'Active' : 'Inactive'}</Text>
                </TouchableOpacity>
              </ScrollView>
            )}
            <InlineMessage
              type={inlineMsg?.type ?? 'error'}
              message={inlineMsg?.message ?? ''}
              visible={!!inlineMsg && !!editItem}
              onDismiss={() => setInlineMsg(null)}
            />
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setEditItem(null)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={saving}>
                <Text style={styles.saveText}>{saving ? 'Saving...' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 8 },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  subText: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.md, fontSize: fontSize.sm },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: 'center', padding: isMobile ? 0 : spacing.lg },
  modalContent: { backgroundColor: c.surface, borderRadius: isMobile ? 0 : borderRadius.lg, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: isMobile ? undefined : 420 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  fieldLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, textTransform: 'uppercase' },
  fieldInput: { backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm, fontSize: fontSize.sm, color: c.text, marginTop: 4, borderWidth: 1, borderColor: c.border },
  toggleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  toggleText: { fontSize: fontSize.md, color: c.text },
  subRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 8 },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm },
  cancelText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  saveText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
  // Subscriber modal extras
  subSearchRow: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm, paddingVertical: 6,
    borderWidth: 1, borderColor: c.border, marginBottom: spacing.sm,
  },
  subSearchInput: { flex: 1, fontSize: fontSize.sm, color: c.text, padding: 0 },
  addSection: {
    marginTop: spacing.md, paddingTop: spacing.sm,
    borderTopWidth: 1, borderTopColor: c.borderLight,
  },
  addSectionTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  userSearchRow: { flexDirection: 'row', gap: spacing.xs },
  userSearchInput: {
    flex: 1, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm, paddingVertical: 6,
    fontSize: fontSize.sm, color: c.text, borderWidth: 1, borderColor: c.border,
  },
  searchBtn: {
    width: rs(36), height: rs(36), borderRadius: borderRadius.sm,
    backgroundColor: c.primary, justifyContent: 'center', alignItems: 'center',
  },
  userResultRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  addBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 2,
    backgroundColor: c.primary, paddingHorizontal: 10, paddingVertical: 4,
    borderRadius: borderRadius.sm,
  },
  addBtnText: { fontSize: fontSize.xs, color: c.white, fontWeight: fontWeight.semibold },
});

const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
